import { intersectionWith, isNil } from 'lodash';
import moment from 'moment';

import type { ProposalListMemberStatus, RealmOutfit, RealmOutfitItem, RealmOutfitItemResource, RealmOutfitTag } from 'types';

export const REGULAR_TAGS_MIN = 1;
export const GTL_TAGS_MIN = 1;

export const DISABLED_OUTFIT_STATUSES = ['published', 'unpublished', 'approved', 'offline'];

export const REALM_OUTFIT_STATUS_DRAFT = 'draft';
export const REALM_OUTFIT_STATUS_APPROVED = 'approved';
export const REALM_OUTFIT_STATUS_PENDING = 'pending';
export const REALM_OUTFIT_STATUS_OFFLINE = 'offline';
export const REALM_OUTFIT_STATUS_DECLINED = 'declined';
export const REALM_OUTFIT_STATUS_PUBLISHED = 'published';
export const REALM_OUTFIT_STATUS_UNPUBLISHED = 'unpublished';

export const REALM_OUTFIT_DOWNLOAD_STATUS_FALLBACK = [
  REALM_OUTFIT_STATUS_PENDING,
  REALM_OUTFIT_STATUS_DECLINED,
  REALM_OUTFIT_STATUS_APPROVED,
  REALM_OUTFIT_STATUS_OFFLINE,
  REALM_OUTFIT_STATUS_PUBLISHED,
  REALM_OUTFIT_STATUS_UNPUBLISHED
];

export const REALM_OUTFIT_DOWNLOADABLE_OUTFIT_STATUS = [
  REALM_OUTFIT_STATUS_PENDING,
  REALM_OUTFIT_STATUS_APPROVED,
  REALM_OUTFIT_STATUS_OFFLINE,
  REALM_OUTFIT_STATUS_PUBLISHED,
  REALM_OUTFIT_STATUS_UNPUBLISHED
];

export const REALM_OUTFIT_DOWNLOADABLE_APPROVED_OUTFIT_STATUS = [
  REALM_OUTFIT_STATUS_APPROVED,
  REALM_OUTFIT_STATUS_OFFLINE,
  REALM_OUTFIT_STATUS_PUBLISHED
];

export const PARTNER_GET_THE_LOOK = 'gtl';
export const REALM_GENDER_OPTIONS = ['women', 'men'];

export const AVAILABLE_LANGUAGES = ['en', 'de'];
export const MAX_CAPTION_LENGTH = 40;

export const ONE_SIZE = 'One Size';

const VIDEO_PROCESSING_FAILED_STATUSES = [
  'UPLOAD_FAIL',
  'TRANSCODE_FAIL',
  'THUMBNAIL_FAIL',
  'PIPELINE_FAIL',
  'PIPELINE_STALLED'
];

export const CAN_PUBLISH_STATUSES = [REALM_OUTFIT_STATUS_PENDING, REALM_OUTFIT_STATUS_UNPUBLISHED];

const SKU_REGEXP = /^[a-zA-Z0-9]{9}-[a-zA-Z0-9]{3}([a-zA-Z0-9]{7})?$/;

export const ITEMS_PER_OUTFIT = {
  value: {
    min: 3,
    max: 12
  },
  set(itemLimist : {min: number, max: number}) {
    this.value = itemLimist;
  },
  get() {
    return this.value;
  }
};


export const hasProposalPage = (status: ProposalListMemberStatus) => status === 'declined' || status === 'approved';
export const getMaxAllowedDateForOutfit = () => moment().add(2, 'years').subtract(1, 'day');

export function isAllowedToEditOutfit(outfit: RealmOutfit) {
  return outfit.status === REALM_OUTFIT_STATUS_PENDING || outfit.status === REALM_OUTFIT_STATUS_DECLINED;
}

export const isRecourceInfoValid = (item: RealmOutfitItemResource): boolean => {
  const { imageUrl, itemUrl, sku, isValid } = item;

  return !isNil(imageUrl) && imageUrl.length > 0
    && !isNil(itemUrl) && itemUrl.length > 0
    && !isNil(sku) && sku.length > 0
    && isValid;
};

export function hasItemWithSameSKU(items: Array<RealmOutfitItemResource | RealmOutfitItem>, sku: string): boolean {
  return (items || []).some(item => item.sku === sku);
}


export const splitTagsByType = (tags: Array<RealmOutfitTag>): { gtl: Array<RealmOutfitTag>, regular: Array<RealmOutfitTag>} =>
  tags.reduce((res, tag) => {
    if (tag.isDeprecated) {
      return res;
    }

    if (tag.type === 'gtl') {
      res.gtl.push(tag);
    }

    if (tag.type === 'regular') {
      res.regular.push(tag);
    }

    return res;
  }, { gtl: [] as Array<RealmOutfitTag>, regular: [] as Array<RealmOutfitTag> });

export const getValidationError = (
  outfit: RealmOutfit,
  availableGTLTags: Array<RealmOutfitTag>,
  availableRegularTags: Array<RealmOutfitTag>,
  realmOutfitAudioTypeEnabled: boolean
): string | null => {
  const itemsLimit = ITEMS_PER_OUTFIT.get();
  const videoTranscodingIncomplete = !isNil(outfit.video) && !outfit.video.isComplete;

  const itemsCount = outfit.items.length;
  const selectedGTLTags = intersectionWith(
    outfit.tags.map(tag => tag.id),
    availableGTLTags.map(tag => tag.id)
  );
  const selectedRegularTags = intersectionWith(
    outfit.tags.map(tag => tag.id),
    availableRegularTags.map(tag => tag.id)
  );

  if (availableRegularTags.length > 0 && selectedRegularTags.length < REGULAR_TAGS_MIN) {
    return 'regular_tags';
  }

  if (availableGTLTags.length > 0 && selectedGTLTags.length < GTL_TAGS_MIN) {
    return 'gtl_tags';
  }

  if (itemsCount < itemsLimit.min || itemsCount > itemsLimit.max) {
    return 'outfit_items';
  }

  if (!outfit.genders.length) {
    return 'influencer_genders';
  }

  if (!outfit.publishedFrom || !outfit.publishedUntil) {
    return 'published_dates';
  }

  if (outfit.publishedCountries?.length === 0) {
    return 'published_countries';
  }

  if (realmOutfitAudioTypeEnabled && !isNil(outfit.video) && isNil(outfit.video.audioType)) {
    return 'audio_type';
  }

  if (videoTranscodingIncomplete) {
    return 'video_transcoding';
  }

  return null;
};

export const isValidSKUFormat = (sku: string): boolean => SKU_REGEXP.test(sku);

export const getOutfitsWithoutDraft = (outfits: Array<RealmOutfit>) => outfits.filter(outfit => outfit.status !== REALM_OUTFIT_STATUS_DRAFT);

export const getDownloadableOutfits = (outfits: Array<RealmOutfit>, onlyApprovedOutfits?: boolean) => {
  const requiredStatuses = onlyApprovedOutfits ? REALM_OUTFIT_DOWNLOADABLE_APPROVED_OUTFIT_STATUS : REALM_OUTFIT_DOWNLOADABLE_OUTFIT_STATUS;

  return outfits.filter(outfit => requiredStatuses.includes(outfit.status));
};

export const shouldVideoPipelineBeRetriggered = (
  pipelineStatus: null | string,
  thumnailUrl: null | string
) => {
  if (pipelineStatus === null) {
    return true;
  }

  if (pipelineStatus === 'THUMBNAIL_READY' && !thumnailUrl) {
    return true;
  }

  return VIDEO_PROCESSING_FAILED_STATUSES.includes(pipelineStatus);
};
